import React, { useMemo } from "react";
import { CBadge, CTooltip } from "@coreui/react";
import { CDataTable } from "@tradingdroid/frontends-components";
import type { ApiMcModeResult } from "@tradingdroid/frontends-types";

import { ColoredValue } from "@src/components/ColoredValue";
import type { WinLossValueType } from "@src/types";

import { McRatingDot, type RatingKind } from "./McRatingDot";

interface Props {
  mode: ApiMcModeResult;
  /** The run's own realized values, keyed by the same metric names. */
  original: Record<string, number>;
  /** The operator's max acceptable drawdown; drawdown cells are rated against it. */
  limitPct: number;
}

type MetricFormat = "quote" | "percent" | "ratio" | "count";

// Only the drawdown-% row carries a rating. The $ drawdown mirrors it, and return / equity / time
// metrics have no absolute good-or-bad, so a traffic light on them would mislead.
const METRIC_META: Record<string, { label: string; format: MetricFormat; rating?: RatingKind }> = {
  max_drawdown_pct_abs: { label: "Max drawdown", format: "percent", rating: "drawdown" },
  max_drawdown_quote: { label: "Max drawdown", format: "quote" },
  min_equity: { label: "Lowest equity", format: "quote" },
  terminal_equity: { label: "Final equity", format: "quote" },
  total_return_pct: { label: "Total return", format: "percent" },
  underwater_trade_ratio: { label: "Time underwater", format: "ratio" },
  longest_underwater_trades: { label: "Longest underwater run", format: "count" },
  max_loss_streak: { label: "Longest losing streak", format: "count" },
};

// Path-shape metrics first: they are the reason to run this at all. The order-invariant ones sink
// to the bottom because their spread is zero by construction.
const METRIC_ORDER = [
  "max_drawdown_pct_abs",
  "max_drawdown_quote",
  "min_equity",
  "underwater_trade_ratio",
  "longest_underwater_trades",
  "max_loss_streak",
  "terminal_equity",
  "total_return_pct",
];

// Map a metric format to ColoredValue's typed, monospaced rendering. "ratio" is a 0..1 fraction shown
// as a percent, so it is scaled here the way the old formatter did.
const coloredFor = (
  value: number | undefined,
  format: MetricFormat,
): { value: number | null; type: WinLossValueType; decimals: number } => {
  const v = value == null || Number.isNaN(value) ? null : value;
  switch (format) {
    case "percent":
      return { value: v, type: "percentage", decimals: 2 };
    case "quote":
      return { value: v, type: "currency", decimals: 2 };
    case "ratio":
      return { value: v == null ? null : v * 100, type: "percentage", decimals: 1 };
    case "count":
      return { value: v, type: "flat", decimals: 0 };
  }
};

interface MetricRow {
  key: string;
  label: string;
  format: MetricFormat;
  rating?: RatingKind;
  invariant: boolean;
  realized: number | undefined;
  mean: number;
  percentiles: Record<string, number>;
}

/** A number cell, prefixed with a rating icon when the metric has a good-or-bad meaning. */
const RatedCell: React.FC<{
  value: number | undefined;
  row: MetricRow;
  limitPct: number;
  bold?: boolean;
}> = ({ value, row, limitPct, bold }) => {
  const c = coloredFor(value, row.format);
  const body = (
    <ColoredValue
      value={c.value}
      type={c.type}
      decimals={c.decimals}
      className={bold ? "fw-bold" : undefined}
    />
  );
  if (row.rating == null || value == null || Number.isNaN(value)) return body;
  return (
    <span className="McRatedCell">
      <McRatingDot kind={row.rating} valuePct={value} limitPct={limitPct} />
      {body}
    </span>
  );
};

/** Per-metric distribution for one resampling mode, with the run's realized value alongside. */
export const McDistributionTable: React.FC<Props> = ({ mode, original, limitPct }) => {
  const invariant = useMemo(() => new Set(mode.invariant_metrics), [mode.invariant_metrics]);

  const rows = useMemo<MetricRow[]>(() => {
    const known = METRIC_ORDER.filter((key) => key in mode.metrics);
    const extra = Object.keys(mode.metrics).filter((key) => !METRIC_ORDER.includes(key));
    return [...known, ...extra].map((key) => {
      const stat = mode.metrics[key];
      return {
        key,
        label: METRIC_META[key]?.label ?? key,
        format: METRIC_META[key]?.format ?? "quote",
        rating: METRIC_META[key]?.rating,
        invariant: invariant.has(key),
        realized: original[key],
        mean: stat.mean,
        percentiles: stat.percentiles,
      };
    });
  }, [mode.metrics, original, invariant]);

  // Percentile columns come from the response rather than a hardcoded list, since the request can
  // ask for any set. Sorted numerically so p5 does not land after p25.
  const percentileKeys = useMemo(() => {
    const first = rows[0];
    if (!first) return [];
    return Object.keys(first.percentiles).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
  }, [rows]);

  const columns = useMemo(
    () => [
      {
        columnKey: "label",
        columnLabel: "Metric",
        columnStyle: { width: "220px" },
        cellRender: (row: MetricRow) => (
          <span className="McMetricLabel">
            {row.label}
            {row.format === "quote" && <span className="text-body-secondary"> ($)</span>}
            {row.invariant && (
              <CTooltip
                content="The resampling cannot change this metric, so every simulation returns the same value."
                placement="top"
              >
                <CBadge color="secondary" className="ms-2 fw-normal">
                  fixed
                </CBadge>
              </CTooltip>
            )}
          </span>
        ),
      },
      {
        columnKey: "realized",
        columnLabel: "This run",
        columnStyle: { width: "110px", textAlign: "right" as const },
        cellRender: (row: MetricRow) => (
          <RatedCell value={row.realized} row={row} limitPct={limitPct} bold />
        ),
      },
      {
        columnKey: "mean",
        columnLabel: "Mean",
        columnStyle: { width: "110px", textAlign: "right" as const },
        cellRender: (row: MetricRow) => (
          <RatedCell value={row.mean} row={row} limitPct={limitPct} />
        ),
      },
      ...percentileKeys.map((percentile) => ({
        columnKey: percentile,
        columnLabel: percentile,
        columnStyle: { width: "100px", textAlign: "right" as const },
        cellRender: (row: MetricRow) => (
          <RatedCell value={row.percentiles[percentile]} row={row} limitPct={limitPct} />
        ),
      })),
    ],
    [percentileKeys, limitPct],
  );

  return (
    <CDataTable
      className="McDistributionTable"
      columns={columns}
      items={rows}
      getRowKey={(row: MetricRow) => row.key}
      striped
      small
      hover
      responsive
    />
  );
};
